'use client'

import type { ErrorInfo } from 'next/error'

import { useEffect, useRef } from 'react'
import { catchError } from 'next/error'

import { PrimaryGradientButton } from '@/components/buttons'
import { MaintenanceContent } from '@/components/maintenance/Maintenance'
import { reportError } from '@/utils/errorLog'

/**
 * Ships the crash to the CMS, then renders nothing.
 *
 * This exists as its own component because `ErrorFallback` below is a plain
 * render function, not a mounted component: reporting from its body would fire
 * again on every re-render and on every `retry()` the user clicks. An effect
 * that remembers WHICH error it reported guards that — a boolean would swallow
 * a second, different crash after `retry()` while the fallback stays mounted.
 * `reportError` additionally de-dupes by message + stack, covering StrictMode's
 * double invocation and a repeat of the same failure.
 */
function ErrorReporter({ error }: { error: unknown }) {
  const reportedError = useRef<unknown>(null)

  useEffect(() => {
    if (reportedError.current === error) return
    reportedError.current = error
    reportError(error)
  }, [error])

  return null
}

/**
 * App-wide error boundary (Next.js 16.3 `catchError`).
 *
 * Chosen over an `app/error.tsx` route boundary for two reasons:
 *   - `catchError` boundaries do NOT swallow `notFound()` / `redirect()`. This
 *     site leans on both (every `[slug]` route calls `notFound()` for unknown
 *     slugs, and `src/app/not-found.tsx` redirects to `/`), so a classic error
 *     boundary around the shell would intercept control flow that is not an
 *     error at all.
 *   - `retry()` re-renders the boundary's children rather than only resetting
 *     client state, so a transient failure can recover in place.
 *
 * The fallback reuses `MaintenanceContent` so a crash looks exactly like the
 * maintenance page, minus its `<main>` wrapper (AppShell already renders one).
 */
function ErrorFallback(_props: { children?: React.ReactNode }, { error, retry }: ErrorInfo) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('[AppShell error boundary]', error)
  }

  return (
    <div className="main section container">
      <ErrorReporter error={error} />
      <MaintenanceContent
        title="Oops! Something went wrong on our end. Please try again."
        action={<PrimaryGradientButton text="Try again" handleClick={() => retry()} />}
      />
    </div>
  )
}

export default catchError(ErrorFallback)
